import React from 'react';
import { buildActivityDescription } from '../../../../utils/activity/buildActivityDescription';
import { filterReturnedAnsweredCallNotes } from '../../../../utils/activity/filterReturnedAnsweredCallNotes';
import { formatDate } from '../../../../utils/dateTime';

interface CrmNote {
  eventId: string;
  eventType: string;
  eventAt: string;
  summary?: string | null;
  metadata?: string | null;
}
interface Props {
  items: CrmNote[];
  total: number;
  page: number;
  hasMore: boolean;
  loading: boolean;
  error: boolean;
  timezone?: string;
  onPrevious: () => void;
  onNext: () => void;
  onRetry: () => void;
}

/** Imported bodies are untrusted plain text. This view has no mutation API. */
export const CrmNotesHistoryView: React.FC<Props> = ({
  items, total, page, hasMore, loading, error, timezone, onPrevious, onNext, onRetry,
}) => {
  const visibleItems = filterReturnedAnsweredCallNotes(items);
  return (
    <section aria-label="CRM notes" className="space-y-3 mt-4 pb-3">
      <h3 className="text-sm font-semibold">CRM notes{!loading && !error ? ` (${total})` : ''}</h3>
      <p className="text-xs text-muted-foreground">Imported from your CRM. Edit these notes in your CRM.</p>
      {error ? (
        <div role="alert" className="text-sm">
          Could not load CRM notes. <button type="button" onClick={onRetry} className="underline">Retry</button>
        </div>
      ) : loading ? (
        <p role="status" className="text-sm">Loading CRM notes…</p>
      ) : visibleItems.length === 0 ? (
        <p className="text-sm text-muted-foreground">No visible CRM notes on this page.</p>
      ) : visibleItems.map((note) => (
        <article key={note.eventId} className="note-card rounded-md p-3">
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs note-card-time">
            <span className="font-semibold">CRM note</span>
            <time dateTime={note.eventAt}>{formatDate(note.eventAt, timezone, 'MMM D, YYYY h:mm A z')}</time>
          </div>
          <div className="text-[13px] note-card-body whitespace-pre-wrap break-words">
            {buildActivityDescription({ ...note, summary: note.summary ?? undefined, metadata: note.metadata ?? undefined })}
          </div>
        </article>
      ))}
      {(page > 0 || hasMore) && (
        <nav aria-label="CRM notes pages" className="flex items-center gap-3 text-sm">
          <button type="button" disabled={loading || page === 0} onClick={onPrevious}>Previous</button>
          <span>Page {page + 1}</span>
          <button type="button" disabled={loading || error || !hasMore} onClick={onNext}>Next</button>
        </nav>
      )}
    </section>
  );
};
